import { URL_REGEX } from 'constants/url.js'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import interpolateRouteUtil from 'modules/routes/utils/interpolate'
import PropTypes from 'prop-types'
import React from 'react'
import Link from 'components/Link'
import './index.scss'

/**
 * Matches a collection of text against a regular expression and then compiles
 * it with a template into parsed elements.
 * @private
 * @param  {(React.Element|String)[]} collection
 * @param  {RegExp} regex
 * @param  {Function} template
 * @returns {(React.Element|String)[]}
 */
function matchAndCompileEach (collection, regex, template) {
  const newCollection = []
  collection.forEach((text) => {
    if (typeof text !== 'string') {
      newCollection.push(text)
      return
    }
    const parts = text.split(regex)
    for (let i = 0; i < parts.length; i++) {
      let part = parts[i]
      part = template(part, i) || part
      if (part) {
        newCollection.push(part)
      }
    }
  })
  return newCollection
}

/**
 * Used to match a userName.
 * 1st group: '@'
 * 2st group: userName
 *
 * @type {RegExp}
 */
const MENTION_REGEX = /@([\w.-]+)/g

function parseMentions (collection) {
  return matchAndCompileEach(collection, MENTION_REGEX,
    (part, index) => {
      if (index % 2 === 1) {
        const to = interpolateRouteUtil(
          ROUTE_PATH.PROFILE_BY_NAME, { name: part }
        )
        return <Link to={to}>@{part}</Link>
      }
    }
  )
}

function parseGymaholicCoWebsiteLink (collection) {
  return matchAndCompileEach(collection,
    /((?:https:\/\/)?(?:www.)?gymaholic.co(?:\/?.*)*)(?:s|.)*/,
    (part, index) => {
      if (index % 2 === 1) {
        return (
          <a
            href={part}
            target='_blank'
            rel='noopener noreferrer'
          >
            {part}
          </a>
        )
      }
    }
  )
}

function parseBitLyShortDownloadLink (collection) {
  return matchAndCompileEach(collection, /(http:\/\/bit.ly\/[a-zA-Z0-9_-]+)/,
    (part, index) => {
      if (index % 2 === 1) {
        return (
          <a
            download
            href={part}
          >{part}
          </a>
        )
      }
    }
  )
}

function parseLinks (collection) {
  return matchAndCompileEach(collection, URL_REGEX.ANY_URL,
    (part, index) => {
      if (index % 2 === 1) {
        return <a href={part}>{part}</a>
      }
    }
  )
}

/**
 * Parses the given text and returns it with the right React elements.
 * @private
 * @param  {string} text
 * @param  {Object} [options={}]
 *                  [options.shouldParseDownloadLink]
 * @returns {ReactElement[]}
 */
function getElements (text, options = {}) {
  let elements = [text]
  elements = parseMentions(elements)
  elements = parseGymaholicCoWebsiteLink(elements)
  if (options.shouldParseDownloadLink) {
    elements = parseBitLyShortDownloadLink(elements)
  }
  if (options.allowLinks) {
    elements = parseLinks(elements)
  }
  return elements.map((chunk, index) => {
    if (typeof chunk !== 'string') {
      return React.cloneElement(chunk, { key: index })
    }
    return chunk
  })
}

class ContentText extends React.Component {
  render () {
    const {
      value,
      shouldParseDownloadLink,
      allowLinks
    } = this.props
    const elements = getElements(value, {
      shouldParseDownloadLink,
      allowLinks
    })
    return (
      <span
        className='contentText'
        style={{
          whiteSpace: 'pre-wrap'
        }}
      >
        {elements}
      </span>
    )
  }
}

ContentText.propTypes = {
  value: PropTypes.string.isRequired,
  shouldParseDownloadLink: PropTypes.bool
}

export default ContentText
